/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { Folders } from "lucide-react"
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react"

import commitSrc from "@/assets/home-new/hosting/commit.svg"
import membersSrc from "@/assets/home-new/hosting/members.svg"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

import { elevatedRingClassName } from "./homeNewStyles"
import { LiveStatusDot } from "./LiveStatusDot"
import { useSectionInView } from "./sectionInView"
import { teamAvatars } from "./teamAvatars"
import { useRevealOnce } from "./useRevealOnce"

// Code hosting showcase: the Engineering org pushing to Entire. Commits with
// fresh SHAs flash in on teammates' rows; each push draws a dashed line into
// that teammate's region, either the primary or one of the mirrors, and a
// mirror that took a push syncs back to the primary along a second line.
// Region dots pulse slowly on their own staggered rhythm. Daniel's row commits
// at a much faster clip than everyone else's. Lines are measured from the live
// layout, so the scene reflows with the panel instead of scaling; narrow
// panels get a compact region column. Styles:
// `.home-new-mirror-*` in home-new.css.

// Each teammate always pushes to the same region, the one closest to them:
// `mirror` indexes MIRRORS, or null for the primary.
const MEMBERS = [
  { name: "Pat Leamon", image: teamAvatars.pat, mirror: 0 },
  { name: "Nina Kaufman", image: teamAvatars.nina, mirror: null },
  { name: "Stefan Haubold", image: teamAvatars.stefan, mirror: 1 },
  { name: "Daniel Adams", image: teamAvatars.daniel, mirror: 1 },
  { name: "Peyton Montei", image: teamAvatars.peyton, mirror: null },
  { name: "Alisha Kawaguchi", image: teamAvatars.alisha, mirror: null },
  { name: "Andrea Nodari", image: teamAvatars.andrea, mirror: 1 },
  { name: "Peter Evjan", image: teamAvatars.peter, mirror: 1 },
  { name: "Sven Pfleiderer", image: teamAvatars.sven, mirror: null },
  { name: "Robin Wohlers-Reichel", image: teamAvatars.robin, mirror: 0 },
] as const

/** Daniel's row: commits land here far more often and leave quickly. */
const FAST_ROW = 3

const PRIMARY = { name: "United States", city: "Ohio" }
const MIRRORS = [
  { name: "Australia", city: "Sydney" },
  { name: "European Union", city: "Frankfurt" },
  { name: "India", city: "Mumbai" },
] as const

// Rows past this index only show from sm up, so the card fits the shorter
// phone panel.
const PHONE_ROWS = 7

// Container width (px) where the full region tiles replace the compact ones;
// matches the `@[34rem]` variants below.
const FULL_LAYOUT_PX = 544

interface Pace {
  every: number // ms between commits
  life: number // ms a SHA stays on its row
  push: number // ms to draw the commit → primary line
  sync: number // ms to draw the mirror → primary line
  lines: number // ms the lines stay up
}

const STEADY: Pace = { every: 1100, life: 3600, push: 650, sync: 650, lines: 1900 }
const RAPID: Pace = { every: 650, life: 480, push: 260, sync: 260, lines: 760 }
const MAX_STEADY_COMMITS = 4

// The design's resting frame, shown without motion.
const STILL_ROWS = [0, 3, 6, 8, 9]

interface Commit {
  id: number
  row: number
  sha: string
  /** The mirror the commit was pushed to, or null when it went to the primary. */
  mirror: number | null
  bornAt: number
  pace: Pace
}

function randomSha() {
  const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz"
  return Array.from(
    { length: 6 },
    () => alphabet[Math.floor(Math.random() * alphabet.length)],
  ).join("")
}

/**
 * The design's region tile: name, city and the live status dot, which pulses
 * slowly, offset by `pulseDelay` seconds. Narrow panels drop to a compact tile
 * with just the city.
 */
function RegionTile({
  name,
  city,
  pulseDelay,
  tileRef,
}: {
  name: string
  city: string
  pulseDelay: number
  tileRef: (element: HTMLDivElement | null) => void
}) {
  return (
    <div
      ref={tileRef}
      className={`flex h-9 min-w-0 items-center gap-2 rounded-lg bg-floating px-2.5 @[34rem]:h-14 @[34rem]:gap-3 @[34rem]:p-3 ${elevatedRingClassName}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm leading-4 whitespace-nowrap">
        <p className="hidden truncate font-semibold text-default @[34rem]:block">{name}</p>
        <p className="truncate text-xs font-semibold text-default @[34rem]:text-sm @[34rem]:font-normal @[34rem]:text-muted">
          {city}
        </p>
      </div>
      <span className="relative flex size-4 shrink-0">
        <span
          className="home-new-mirror-pulse"
          style={{ animationDelay: `${pulseDelay}s` }}
          aria-hidden="true"
        />
        <LiveStatusDot />
      </span>
    </div>
  )
}

interface Geometry {
  width: number
  height: number
  cardRight: number
  rowY: number[]
  primaryLeft: { x: number; y: number }
  primaryRight: { x: number; y: number }
  mirrorLeft: Array<{ x: number; y: number }>
  mirrorRight: Array<{ x: number; y: number }>
}

/** Measures anchor points (relative to the scene) whenever the scene resizes. */
function useGeometry(
  scene: RefObject<HTMLDivElement | null>,
  card: RefObject<HTMLDivElement | null>,
  rows: RefObject<Array<HTMLDivElement | null>>,
  primary: RefObject<HTMLDivElement | null>,
  mirrors: RefObject<Array<HTMLDivElement | null>>,
) {
  const [geometry, setGeometry] = useState<Geometry | null>(null)

  useLayoutEffect(() => {
    const sceneElement = scene.current
    if (!sceneElement) return
    const measure = () => {
      const base = sceneElement.getBoundingClientRect()
      const cardElement = card.current
      const primaryElement = primary.current
      if (!cardElement || !primaryElement) return
      const middle = (rect: DOMRect) => rect.top + rect.height / 2 - base.top
      const primaryRect = primaryElement.getBoundingClientRect()
      setGeometry({
        width: base.width,
        height: base.height,
        cardRight: cardElement.getBoundingClientRect().right - base.left,
        rowY: rows.current.map((row) => (row ? middle(row.getBoundingClientRect()) : 0)),
        primaryLeft: { x: primaryRect.left - base.left, y: middle(primaryRect) },
        primaryRight: { x: primaryRect.right - base.left, y: middle(primaryRect) },
        mirrorLeft: mirrors.current.map((tile) => {
          const rect = tile?.getBoundingClientRect()
          return rect ? { x: rect.left - base.left, y: middle(rect) } : { x: 0, y: 0 }
        }),
        mirrorRight: mirrors.current.map((tile) => {
          const rect = tile?.getBoundingClientRect()
          return rect ? { x: rect.right - base.left, y: middle(rect) } : { x: 0, y: 0 }
        }),
      })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(sceneElement)
    return () => observer.disconnect()
  }, [scene, card, rows, primary, mirrors])

  return geometry
}

/**
 * An orthogonal connector with rounded corners whose vertical run sits exactly
 * halfway between its two ends.
 */
function elbowPath(x1: number, y1: number, x2: number, y2: number) {
  if (Math.abs(y2 - y1) < 1) return `M ${x1} ${y1} H ${x2}`
  const radius = Math.min(8, Math.abs(y2 - y1) / 2)
  const dir = y2 > y1 ? 1 : -1
  const mid = (x1 + x2) / 2
  return [
    `M ${x1} ${y1}`,
    `H ${mid - radius}`,
    `Q ${mid} ${y1} ${mid} ${y1 + dir * radius}`,
    `V ${y2 - dir * radius}`,
    `Q ${mid} ${y2} ${mid + radius} ${y2}`,
    `H ${x2}`,
  ].join(" ")
}

/** A loop off the right edges, starting at `fromY` and ending at `toY`. */
function loopPath(x: number, fromY: number, toY: number, reach: number) {
  const out = x + reach
  const radius = Math.min(8, reach / 2)
  const dir = toY > fromY ? 1 : -1
  return [
    `M ${x} ${fromY}`,
    `H ${out - radius}`,
    `Q ${out} ${fromY} ${out} ${fromY + dir * radius}`,
    `V ${toY - dir * radius}`,
    `Q ${out} ${toY} ${out - radius} ${toY}`,
    `H ${x}`,
  ].join(" ")
}

/** A dashed link that draws itself in over `duration` after `delay`, then flows. */
function FlowLine({
  d,
  delay,
  duration,
  life,
  id,
}: {
  d: string
  delay: number
  duration: number
  life: number
  id: string
}) {
  return (
    <g className="home-new-mirror-line" style={{ animationDuration: `${life}ms` }}>
      <mask id={id} maskUnits="userSpaceOnUse">
        <path
          d={d}
          pathLength={1}
          stroke="white"
          strokeWidth={4}
          fill="none"
          className="home-new-mirror-draw"
          style={{ animationDelay: `${delay}ms`, animationDuration: `${duration}ms` }}
        />
      </mask>
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeDasharray="4 4"
        strokeLinejoin="round"
        mask={`url(#${id})`}
        className="home-new-mirror-dash"
      />
    </g>
  )
}

export function CommitMirrorShowcase() {
  const sceneRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Array<HTMLDivElement | null>>([])
  const primaryRef = useRef<HTMLDivElement>(null)
  const mirrorRefs = useRef<Array<HTMLDivElement | null>>([])
  const geometry = useGeometry(sceneRef, cardRef, rowRefs, primaryRef, mirrorRefs)

  const [active, observe] = useRevealOnce("0px 0px -20% 0px")

  // Timers stop while the section is offscreen and resume with the same
  // commits; ids keep counting so a resumed scene never reuses a key.
  const inView = useSectionInView()
  const nextIdRef = useRef(1)
  const running = active && inView

  const [commits, setCommits] = useState<Commit[]>([])
  const [now, setNow] = useState(0)
  const [still, setStill] = useState(false)

  const prefersReducedMotion = usePrefersReducedMotion()
  // Spawn and retire commits on timers once the panel is in view.
  useEffect(() => {
    if (!running) return
    if (prefersReducedMotion) {
      const frame = window.requestAnimationFrame(() => setStill(true))
      return () => window.cancelAnimationFrame(frame)
    }

    const make = (row: number, pace: Pace): Commit => ({
      id: nextIdRef.current++,
      row,
      sha: randomSha(),
      mirror: MEMBERS[row]!.mirror,
      bornAt: performance.now(),
      pace,
    })

    const spawnSteady = () => {
      setCommits((current) => {
        const at = performance.now()
        const alive = current.filter((commit) => at - commit.bornAt < commit.pace.life)
        const steady = alive.filter((commit) => commit.row !== FAST_ROW)
        if (steady.length >= MAX_STEADY_COMMITS) return alive
        const taken = new Set(alive.map((commit) => commit.row))
        const rowCount = window.matchMedia("(min-width: 40rem)").matches
          ? MEMBERS.length
          : PHONE_ROWS
        const free = Array.from({ length: rowCount }, (_, row) => row).filter(
          (row) => row !== FAST_ROW && !taken.has(row),
        )
        if (free.length === 0) return alive
        const commit = make(free[Math.floor(Math.random() * free.length)]!, STEADY)
        return [...alive, commit]
      })
      setNow(performance.now())
    }

    const spawnRapid = () => {
      setCommits((current) => {
        const at = performance.now()
        const alive = current.filter(
          (commit) => commit.row !== FAST_ROW && at - commit.bornAt < commit.pace.life,
        )
        const commit = make(FAST_ROW, RAPID)
        return [...alive, commit]
      })
      setNow(performance.now())
    }

    const first = window.setTimeout(spawnSteady, 200)
    const steadyTimer = window.setInterval(spawnSteady, STEADY.every)
    const rapidTimer = window.setInterval(spawnRapid, RAPID.every)
    // A coarse clock so expired commits and lines fade out between spawns.
    const clock = window.setInterval(() => setNow(performance.now()), 120)
    return () => {
      window.clearTimeout(first)
      window.clearInterval(steadyTimer)
      window.clearInterval(rapidTimer)
      window.clearInterval(clock)
    }
  }, [running, prefersReducedMotion])

  const latestOnRow = (row: number) => commits.findLast((commit) => commit.row === row)
  const reach = geometry && geometry.width >= FULL_LAYOUT_PX ? 24 : 12

  return (
    <div
      ref={observe}
      className="@container absolute inset-0 flex items-center justify-center px-4 @[34rem]:px-6"
    >
      <div
        ref={sceneRef}
        className="relative flex w-full max-w-[709px] items-center justify-center gap-5 @[34rem]:gap-12 @[52rem]:gap-[107px]"
      >
        {/* The org card: Engineering and its people, each row a place a commit can land. */}
        <div
          ref={cardRef}
          className={`relative z-10 min-w-0 flex-1 overflow-hidden rounded-lg bg-floating pb-1 font-sans @[34rem]:max-w-[362px] ${elevatedRingClassName}`}
        >
          <div className="flex items-center gap-2 px-3 py-[11px]">
            <span className="flex size-6 shrink-0 items-center justify-center">
              <Folders size={16} strokeWidth={1.33} className="text-(--neutral-500)" />
            </span>
            <p className="min-w-0 flex-1 truncate text-sm leading-4 font-semibold text-default">
              Engineering
            </p>
            <span className="flex shrink-0 items-center gap-1 text-xs leading-3 text-muted">
              <img src={membersSrc} alt="" width={16} height={16} />
              42
            </span>
          </div>
          {MEMBERS.map((member, row) => {
            const commit = latestOnRow(row)
            // Resting rows only while motion stays reduced; `still` is left
            // behind when the preference flips back, and the timers take over.
            const showing =
              still && prefersReducedMotion
                ? STILL_ROWS.includes(row)
                : commit !== undefined && now - commit.bornAt < commit.pace.life
            return (
              <div
                key={member.name}
                ref={(element) => {
                  rowRefs.current[row] = element
                }}
                className={`flex h-9 items-center gap-2 px-3 ${row >= PHONE_ROWS ? "hidden sm:flex" : ""}`}
              >
                <img
                  src={member.image}
                  alt=""
                  width={24}
                  height={24}
                  loading="lazy"
                  className="size-6 shrink-0 rounded-full object-cover"
                />
                <p className="min-w-0 flex-1 truncate text-sm leading-5 tracking-[-0.01em] text-default">
                  {member.name}
                </p>
                <span
                  data-showing={showing ? "true" : "false"}
                  className={`home-new-mirror-commit flex shrink-0 items-center gap-3 ${
                    row === FAST_ROW ? "home-new-mirror-commit-rapid" : ""
                  }`}
                >
                  <span className="font-mono text-xs leading-5 text-muted @[34rem]:text-[13px]">
                    {commit?.sha ?? "fr45wl"}
                  </span>
                  <img
                    src={commitSrc}
                    alt=""
                    width={16}
                    height={16}
                    className="hidden @[34rem]:block"
                  />
                </span>
              </div>
            )
          })}
        </div>

        {/* Regions: where pushes land and mirrors sync from. Compact on narrow panels. */}
        <div className="relative z-10 flex w-[104px] shrink-0 flex-col gap-4 font-sans @[34rem]:w-[200px] @[34rem]:gap-6 @[44rem]:w-[240px]">
          <div className="flex flex-col gap-2 @[34rem]:gap-3">
            <p className="text-xs leading-4 font-semibold text-white/75">
              Primary<span className="hidden @[34rem]:inline"> region</span>
            </p>
            <RegionTile
              {...PRIMARY}
              pulseDelay={0}
              tileRef={(element) => {
                primaryRef.current = element
              }}
            />
          </div>
          <div className="flex flex-col gap-2 @[34rem]:gap-3">
            <p className="text-xs leading-4 font-semibold text-white/75">
              Mirror<span className="hidden @[34rem]:inline">ed region</span>s
            </p>
            {MIRRORS.map((region, index) => (
              <RegionTile
                key={region.name}
                {...region}
                pulseDelay={0.9 * (index + 1)}
                tileRef={(element) => {
                  mirrorRefs.current[index] = element
                }}
              />
            ))}
          </div>
        </div>

        {geometry ? (
          <svg
            className="pointer-events-none absolute inset-0 z-0 overflow-visible text-white/60"
            width={geometry.width}
            height={geometry.height}
            aria-hidden="true"
          >
            {commits
              .filter((commit) => now - commit.bornAt < commit.pace.lines)
              .map((commit) => {
                const rowY = geometry.rowY[commit.row]
                if (rowY === undefined) return null
                const target =
                  commit.mirror === null ? geometry.primaryLeft : geometry.mirrorLeft[commit.mirror]
                const mirror = commit.mirror === null ? null : geometry.mirrorRight[commit.mirror]
                if (!target) return null
                return (
                  <g key={commit.id}>
                    <FlowLine
                      id={`home-new-mirror-push-${commit.id}`}
                      d={elbowPath(geometry.cardRight, rowY, target.x, target.y)}
                      delay={0}
                      duration={commit.pace.push}
                      life={commit.pace.lines}
                    />
                    {mirror ? (
                      <FlowLine
                        id={`home-new-mirror-sync-${commit.id}`}
                        d={loopPath(
                          geometry.primaryRight.x,
                          mirror.y,
                          geometry.primaryRight.y,
                          reach,
                        )}
                        delay={commit.pace.push}
                        duration={commit.pace.sync}
                        life={commit.pace.lines}
                      />
                    ) : null}
                  </g>
                )
              })}
          </svg>
        ) : null}
      </div>
    </div>
  )
}
